<template>
    <div class="kyt-empty-state">
        <div class="empty-state-content">
            <!-- Icon -->
            <div class="empty-state-icon mb-3">
                <vue-feather 
                    :type="icon" 
                    :size="iconSize" 
                    :class="`text-${iconColor}`"
                ></vue-feather>
            </div>
            
            <!-- Title -->
            <h5 class="empty-state-title">{{ title }}</h5>
            
            <!-- Description -->
            <p v-if="description" class="empty-state-description text-muted">
                {{ description }}
            </p>
            
            <!-- Action Button -->
            <div v-if="showButton && buttonText" class="empty-state-action mt-3">
                <button 
                    @click="handleButtonClick" 
                    class="btn"
                    :class="buttonClass"
                >
                    <vue-feather 
                        v-if="buttonIcon" 
                        :type="buttonIcon" 
                        class="me-1" 
                        size="16"
                    ></vue-feather>
                    {{ buttonText }}
                </button>
            </div>
            
            <!-- Custom slot for additional content -->
            <div v-if="$slots.default" class="empty-state-custom mt-3">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    icon?: string;
    iconSize?: string | number;
    iconColor?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'muted';
    title: string;
    description?: string;
    showButton?: boolean;
    buttonText?: string;
    buttonIcon?: string;
    buttonVariant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'outline-primary' | 'outline-secondary';
}

interface Emits {
    (e: 'button-click'): void;
}

const props = withDefaults(defineProps<Props>(), {
    icon: 'search',
    iconSize: 48,
    iconColor: 'muted',
    showButton: false,
    buttonVariant: 'primary'
});

const emit = defineEmits<Emits>();

const buttonClass = computed(() => {
    return `btn-${props.buttonVariant}`;
});

const handleButtonClick = () => {
    emit('button-click');
};
</script>

<script lang="ts">
import { computed } from 'vue';

export default {
    name: 'KytChainEmptyState'
};
</script>

<style scoped>
.kyt-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    min-height: 300px;
}

.empty-state-content {
    text-align: center;
    max-width: 400px;
}

.empty-state-icon {
    opacity: 0.6;
}

.empty-state-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.empty-state-description {
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0;
}

.empty-state-action .btn {
    padding: 0.5rem 1.5rem;
    font-weight: 500;
}

/* Hover effects */
.empty-state-action .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease-in-out;
}

/* Responsive adjustments */
@media (max-width: 576px) {
    .kyt-empty-state {
        padding: 2rem 1rem;
        min-height: 250px;
    }
    
    .empty-state-title {
        font-size: 1.1rem;
    }
    
    .empty-state-description {
        font-size: 0.8rem;
    }
}

/* Icon color variants */
.text-muted {
    color: #6c757d !important;
}

.text-primary {
    color: #007bff !important;
}

.text-secondary {
    color: #6c757d !important;
}

.text-success {
    color: #28a745 !important;
}

.text-danger {
    color: #dc3545 !important;
}

.text-warning {
    color: #ffc107 !important;
}

.text-info {
    color: #17a2b8 !important;
}

.text-light {
    color: #f8f9fa !important;
}

.text-dark {
    color: #343a40 !important;
}
</style>
